import { createTheme, PaletteMode, Stack, Typography } from '@mui/material';
import { itIT } from '@mui/material/locale';
import { itIT as gridItIT } from '@mui/x-data-grid/locales';
import { isTablet } from 'react-device-detect';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { itIT as datePickersIT } from '@mui/x-date-pickers/locales';
import { CustomGridLoader } from './themeGridLoader';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';

const checkIpadOs = function () {
  const ua = window.navigator.userAgent;

  if (ua.indexOf('Macintosh') > -1) {
    try {
      document.createEvent('TouchEvent');
      return true; // Il dispositivo è un iPadOS
    } catch (e: any) {
      // Il blocco catch cattura l'errore se l'evento TouchEvent non può essere creato
    }
  }

  return false; // Il dispositivo non è un iPadOS
};

const isTabletView = isTablet || checkIpadOs();

export function getBludataTheme(themeMode: PaletteMode) {
  return createTheme({
    ...itIT,
    ...datePickersIT,
    ...gridItIT,
    palette: {
      mode: themeMode,
      primary: {
        main: '#0d4da1',
        light: '#6692d3',
        dark: '#09397f',
      },
      error: {
        main: '#bc3c44',
        light: '#c96369',
        dark: '#832a2f',
        contrastText: '#fff',
      },
      success: {
        main: '#2e7d32',
        light: '#d4edda',
        dark: '#1b5e20',
        contrastText: '#fff',
      },
      warning: {
        main: '#ed6c02',
        light: '#ffffe0',
        dark: '#e65100',
        contrastText: '#fff',
      },
    },
    typography: {
      allVariants: {
        color: 'var(--color)',
      },
      button: {
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: '600',
        letterSpacing: 'none',
      },
    },
    components: {
      MuiTablePagination: {
        defaultProps: {
          slotProps: {
            select: {
              MenuProps: {
                disablePortal: false,
              },
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            overflow: 'auto',
          },
          cell: {
            outline: 'none!important',
            textOverflow: 'clip',
            display: 'flex',
            alignItems: 'center',
          },
          row: {
            minHeight: '36px !important',
          },
          overlayWrapperInner: {
            display: 'flex',
          },
        },
        defaultProps: {
          disableColumnMenu: true,
          disableVirtualization: true,
          density: 'compact',
          columnVisibilityModel: {
            id: false, // Nascondi la colonna ID per impostazione predefinita
          },
          slots: {
            loadingOverlay: () => <CustomGridLoader />,
            noRowsOverlay: () => (
              <Stack flex={1} alignItems="center" justifyContent="center">
                <Typography>Nessuna riga disponibile</Typography>
              </Stack>
            ),
          },

          localeText: {
            footerTotalRows: 'Righe totali: ',
            noRowsLabel: 'Nessuna riga disponibile',
            footerRowSelected: (value) => {
              return '';
            },
            paginationDisplayedRows: ({ from, to, count, estimated }) => {
              if (count <= 1) return ``;
              return `${from} – ${to}`;
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: (options) => {
            const { ownerState, theme } = options;
            const defaultOverride = {
              padding: isTablet ? '0px 8px' : '3px 16px',
              justifyContent: 'start',
              borderRadius: '20px',
            };

            if (
              ownerState.variant === 'outlined' &&
              theme.palette.mode === 'dark'
            ) {
              return {
                ...defaultOverride,
                color: 'white',
                borderColor: 'var(--input-border-color) !important',
              };
            }
            if (
              ownerState.variant === 'text' &&
              theme.palette.mode === 'dark'
            ) {
              return {
                ...defaultOverride,
                color: 'white',
              };
            }
            return defaultOverride;
          },
        },
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const defaultOverride = {};
            if (theme.palette.mode === 'dark') {
              return {
                ...defaultOverride,
                color: 'var(--color) !important',
              };
            }
            return defaultOverride;
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const defaultOverride = {};
            if (theme.palette.mode === 'dark') {
              return {
                ...defaultOverride,
                color: 'var(--color) !important',
              };
            }
            return defaultOverride;
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => {
            if (theme.palette.mode === 'dark') {
              return {
                color: 'white',
              };
            }
            return {};
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--base-foreground-color)',
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          //? HA FIXATO COSE, NON TOCCARE!
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          fullScreen: isTabletView,
          PaperProps: {
            noValidate: true,
            autoComplete: 'off', // Imposta l'autocomplete per tutti i dialog come form
          },
          sx: {
            '& .MuiDialog-paper': {
              borderRadius: '8px !important',
            },
          },
        },
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--background-color) !important',
            backgroundImage: 'none!important',
          },
        },
      },
      /*        Tema dialog sencha*/ MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--base-color)',
            color: 'var(--reverse-color)',
            marginBottom: '10px',
            padding: '12px 16px',
            lineHeight: '1.3em',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 'unset !important',
            '& .MuiTab-root': {
              borderBottom: '1px solid var(--base-color)',
              minHeight: 'unset !important',
              padding: '8px 16px !important',
              borderTopRightRadius: '12px !important',
              borderTopLeftRadius: '0px !important',
            },
            '.bludata-bottom-tab-panel& .MuiTab-root': {
              borderBottom: '1px solid var(--base-color)',
              minHeight: 'unset !important',
              padding: '4px 16px !important',
              borderTopRightRadius: '0px !important',
              borderTopLeftRadius: '0px !important',
            },
            '& .MuiTabs-flexContainer': {
              alignItems: 'end',
            },
            '& .MuiTabs-flexContainer:after': {
              content: '""',
              height: '1px',
              backgroundColor: 'var(--base-color)',
              flex: 1,
            },
            '& .Mui-selected': {
              border: '1px solid var(--base-color)',
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              borderBottom: '0px solid',
              color: 'var(--base-color) !important',
            },
          },
          indicator: {
            display: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
          autoComplete: 'off',
          spellCheck: false,
          inputProps: {
            spellCheck: false,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variant: 'body2',
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: 'standard',
          MenuProps: {
            disablePortal: true, // Accessibilita: fix VoiceOver iOS/Mac
            PaperProps: {
              style: {
                maxHeight: 350,
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: 'var(--color)',
            fontSize: '0.875rem !important',
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          size: 'small',
          autoComplete: 'off',
        },
        styleOverrides: {
          root: {
            marginTop: '0px !important',
            border: '1px solid var(--input-border-color)',
            borderRadius: '4px',
            paddingLeft: '5px',
            paddingTop: '2px',
            paddingRight: '5px',
            fontWeight: 'bold',
            ':before': {
              opacity: '0',
            },
            '& input::placeholder': {
              fontWeight: 400,
              fontStyle: 'italic',
            },
            '& textarea::placeholder': {
              fontWeight: 400,
              fontStyle: 'italic',
            },
            // Sentinella CSS per rilevamento autofill del browser.
            // Quando il browser autofilla un campo, questa animazione parte
            // e l'evento animationstart viene intercettato da AutofillDetector.
            '& input:-webkit-autofill': {
              animationName: 'onAutoFillStart',
              animationDuration: '0.01s',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            transform: 'none !important',
            color: 'var(--color) !important',
            position: 'relative',
            fontSize: '0.875rem',
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          autoHighlight: false,
          selectOnFocus: false,
          autoSelect: false,
          clearOnEscape: true,
          openOnFocus: true,
          noOptionsText: ' ',
          disablePortal: true, // Accessibilita: fix VoiceOver iOS/Mac (MUI issue #22956)
          slotProps: {
            popper: {
              popperOptions: {
                strategy: 'fixed', // Evita clipping dropdown dentro Dialog con overflow
              },
            },
          },
        },
      },
      MuiPickersTextField: {
        defaultProps: {
          variant: 'standard',
          size: 'small',
        },
        styleOverrides: {
          root: {
            width: 230,
            marginTop: '0px !important',
            fontWeight: 'bold',
            boxSizing: 'border-box',
            '& .MuiPickersInputBase-root': {
              marginTop: '0px !important',
              border: '1px solid var(--input-border-color)',
              borderRadius: '4px',
            },

            '& .MuiPickersInputBase-root::before': {
              opacity: '0',
            },

            '& .MuiPickersInputBase-sectionContent[aria-valuetext="Vuoto"]': {
              fontWeight: 400,
              fontStyle: 'italic',
            },

            '& .MuiInput-root': {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: '5px',
              paddingRight: '0px',
              // borderColor: 'var(--input-border-color) !important',
            },
            '& .MuiInput-input': {
              padding: '0px',
            },
            '& .Mui-disabled': {
              color: 'rgba(0, 0, 0, 0.38)',
            },

            '& .MuiInputAdornment-root': {
              marginLeft: '0px',
            },

            '& .MuiPickersInputBase-sectionsContainer': {
              fontWeight: 'bold',
              paddingLeft: '5px',
              paddingBottom: '0px',
            },
          },
        },
      },
    },
  });
}
